import { Suspense, lazy } from 'react'
import { Outlet } from 'react-router-dom'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MainLayout } from '@/components/layout/main-layout'
import { ProtectedRoute, AdminRoute } from '@/components/protected-route'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorBoundary, RouteErrorFallback } from '@/components/error'
import { MaintenanceShell } from '@/components/maintenance'

const HomePage = lazy(() => import('@/pages/home').then(m => ({ default: m.HomePage })))
const AuthPage = lazy(() => import('@/pages/auth').then(m => ({ default: m.AuthPage })))
const AuthCallbackPage = lazy(() => import('@/pages/auth-callback').then(m => ({ default: m.AuthCallbackPage })))
const AuthPluginPage = lazy(() => import('@/pages/auth-plugin').then(m => ({ default: m.AuthPluginPage })))
const TermsPage = lazy(() => import('@/pages/terms').then(m => ({ default: m.TermsPage })))
const PrivacyPage = lazy(() => import('@/pages/privacy').then(m => ({ default: m.PrivacyPage })))
const ProfileOnboardingPage = lazy(() =>
  import('@/pages/onboarding/profile').then(m => ({ default: m.ProfileOnboardingPage })),
)
const ExplorePage = lazy(() => import('@/pages/explore').then(m => ({ default: m.ExplorePage })))
const SearchPage = lazy(() => import('@/pages/search').then(m => ({ default: m.SearchPage })))
const CreatorsPage = lazy(() => import('@/pages/creators').then(m => ({ default: m.CreatorsPage })))
const GuidesPage = lazy(() => import('@/pages/guides').then(m => ({ default: m.GuidesPage })))
const NewGuidePage = lazy(() => import('@/pages/guides/new').then(m => ({ default: m.NewGuidePage })))
const GuideDetailPage = lazy(() => import('@/pages/guides/$slug').then(m => ({ default: m.GuideDetailPage })))
const EditGuidePage = lazy(() => import('@/pages/guides/edit/$slug').then(m => ({ default: m.EditGuidePage })))
const WorkflowDetailPage = lazy(() => import('@/pages/workflow/$id').then(m => ({ default: m.WorkflowDetailPage })))
const CollectionDetailPage = lazy(() =>
  import('@/pages/collection/$id').then(m => ({ default: m.CollectionDetailPage })),
)
const NewWorkflowPage = lazy(() => import('@/pages/workflow/new').then(m => ({ default: m.NewWorkflowPage })))
const EditWorkflowPage = lazy(() => import('@/pages/workflow/edit/$id').then(m => ({ default: m.EditWorkflowPage })))
const ProfilePage = lazy(() => import('@/pages/profile/$username').then(m => ({ default: m.ProfilePage })))
const ProfileSettingsPage = lazy(() => import('@/pages/profile/settings').then(m => ({ default: m.ProfileSettingsPage })))
const NotificationsPage = lazy(() =>
  import('@/pages/notifications').then(m => ({ default: m.NotificationsPage })),
)
const SupportPage = lazy(() => import('@/pages/support').then(m => ({ default: m.SupportPage })))
const DashboardIndexPage = lazy(() => import('@/pages/dashboard/index').then(m => ({ default: m.DashboardIndexPage })))
const DashboardWorkflowsPage = lazy(() => import('@/pages/dashboard/workflows').then(m => ({ default: m.DashboardWorkflowsPage })))
const DashboardCollectionsPage = lazy(() => import('@/pages/dashboard/collections').then(m => ({ default: m.DashboardCollectionsPage })))
const DashboardAnalyticsPage = lazy(() => import('@/pages/dashboard/analytics').then(m => ({ default: m.DashboardAnalyticsPage })))
const DashboardFavoritesPage = lazy(() => import('@/pages/dashboard/favorites').then(m => ({ default: m.DashboardFavoritesPage })))
const DashboardFollowingPage = lazy(() => import('@/pages/dashboard/following').then(m => ({ default: m.DashboardFollowingPage })))
const PluginPage = lazy(() => import('@/pages/dashboard/plugin').then(m => ({ default: m.PluginPage })))
const ComfyuiPluginPreviewPage = lazy(() =>
  import('@/pages/dev/comfyui-plugin-preview').then(m => ({ default: m.ComfyuiPluginPreviewPage })),
)
const ForumIndexPage = lazy(() => import('@/pages/forum/index').then(m => ({ default: m.ForumIndexPage })))
const ForumCategoryPage = lazy(() => import('@/pages/forum/category/$slug').then(m => ({ default: m.ForumCategoryPage })))
const ForumThreadPage = lazy(() => import('@/pages/forum/thread/$id').then(m => ({ default: m.ForumThreadPage })))
const NewThreadPage = lazy(() => import('@/pages/forum/new').then(m => ({ default: m.NewThreadPage })))

const FallbackComponent = () => <div className="p-4 text-center text-muted-foreground">Loading...</div>

// Admin pages
const AdminDashboardPage = lazy(() => import('@/pages/admin/index').then(m => ({ default: m.default || FallbackComponent })))
const AdminUsersPage = lazy(() => import('@/pages/admin/users').then(m => ({ default: m.default || FallbackComponent })))
const AdminUserDetailPage = lazy(() =>
  import('@/pages/admin/user-detail').then(m => ({ default: m.default || FallbackComponent })),
)
const AdminWorkflowsPage = lazy(() => import('@/pages/admin/workflows').then(m => ({ default: m.default || FallbackComponent })))
const AdminGuidesPage = lazy(() => import('@/pages/admin/guides').then(m => ({ default: m.default || FallbackComponent })))
const AdminForumsPage = lazy(() => import('@/pages/admin/forums').then(m => ({ default: m.default || FallbackComponent })))
const AdminReportsPage = lazy(() => import('@/pages/admin/reports').then(m => ({ default: m.default || FallbackComponent })))
const AdminReportsExportPage = lazy(() => import('@/pages/admin/reports-export').then(m => ({ default: m.default || FallbackComponent })))
const AdminRolesPage = lazy(() => import('@/pages/admin/roles').then(m => ({ default: m.default || FallbackComponent })))
const AdminAuditPage = lazy(() => import('@/pages/admin/audit').then(m => ({ default: m.default || FallbackComponent })))
const AdminEmailPage = lazy(() => import('@/pages/admin/email').then(m => ({ default: m.default || FallbackComponent })))
const AdminSettingsPage = lazy(() => import('@/pages/admin/settings').then(m => ({ default: m.default || FallbackComponent })))
const AdminBanMessagesPage = lazy(() =>
  import('@/pages/admin/ban-messages').then(m => ({ default: m.default || FallbackComponent })),
)
const AdminUserSupportPage = lazy(() =>
  import('@/pages/admin/support').then(m => ({ default: m.default || FallbackComponent })),
)
const AdminAppFeedbackPage = lazy(() =>
  import('@/pages/admin/feedback').then(m => ({ default: m.default || FallbackComponent })),
)
const AdminLivePresencePage = lazy(() =>
  import('@/pages/admin/live-presence').then(m => ({ default: m.default || FallbackComponent })),
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 1,
    },
  },
})

const PageSkeleton = () => (
  <div className="container mx-auto px-4 py-8">
    <div className="space-y-4">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-4 w-48" />
      <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Skeleton key={i} className="h-48" />
        ))}
      </div>
    </div>
  </div>
)

const withSkeleton = (Element: React.ComponentType) => (
  <Suspense fallback={<PageSkeleton />}>
    <Element />
  </Suspense>
)

const router = createBrowserRouter([
  {
    element: <MaintenanceShell />,
    errorElement: <RouteErrorFallback />,
    children: [
  {
    path: '/',
    element: <MainLayout />,
    children: [
      {
        index: true,
        element: withSkeleton(HomePage),
      },
      {
        path: 'explore',
        element: withSkeleton(ExplorePage),
      },
      {
        path: 'search',
        element: withSkeleton(SearchPage),
      },
      {
        path: 'creators',
        element: withSkeleton(CreatorsPage),
      },
      {
        path: 'guides',
        element: withSkeleton(GuidesPage),
      },
      {
        path: 'guides/:slug',
        element: withSkeleton(GuideDetailPage),
      },
      {
        path: 'workflow/:id',
        element: withSkeleton(WorkflowDetailPage),
      },
      {
        path: 'collection/:id',
        element: withSkeleton(CollectionDetailPage),
      },
      {
        path: 'profile/:username',
        element: withSkeleton(ProfilePage),
      },
      {
        path: 'forum',
        element: withSkeleton(ForumIndexPage),
      },
      {
        path: 'forum/:slug',
        element: withSkeleton(ForumCategoryPage),
      },
      {
        path: 'forum/thread/:id',
        element: withSkeleton(ForumThreadPage),
      },
      {
        element: (
          <ProtectedRoute requireAuth>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            path: 'guides/new',
            element: withSkeleton(NewGuidePage),
          },
          {
            path: 'guides/:slug/edit',
            element: withSkeleton(EditGuidePage),
          },
          {
            path: 'workflow/new',
            element: withSkeleton(NewWorkflowPage),
          },
          {
            path: 'workflow/:id/edit',
            element: withSkeleton(EditWorkflowPage),
          },
          {
            path: 'profile/settings',
            element: withSkeleton(ProfileSettingsPage),
          },
          {
            path: 'support',
            element: withSkeleton(SupportPage),
          },
          {
            path: 'notifications',
            element: withSkeleton(NotificationsPage),
          },
          {
            path: 'forum/new',
            element: withSkeleton(NewThreadPage),
          },
        ],
      },
    ],
  },
  {
    path: '/auth',
    element: withSkeleton(AuthPage),
  },
  {
    path: '/auth/callback',
    element: withSkeleton(AuthCallbackPage),
  },
  {
    path: '/auth/plugin',
    element: withSkeleton(AuthPluginPage),
  },
  ...(import.meta.env.DEV
    ? [
        {
          path: '/dev/comfyui-plugin',
          element: withSkeleton(ComfyuiPluginPreviewPage),
        },
      ]
    : []),
  {
    path: '/terms',
    element: withSkeleton(TermsPage),
  },
  {
    path: '/privacy',
    element: withSkeleton(PrivacyPage),
  },
  {
    path: '/onboarding/profile',
    element: (
      <ProtectedRoute requireAuth>
        <Suspense fallback={<PageSkeleton />}>
          <ProfileOnboardingPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute requireAuth>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: withSkeleton(DashboardIndexPage),
      },
      {
        path: 'workflows',
        element: withSkeleton(DashboardWorkflowsPage),
      },
      {
        path: 'collections',
        element: withSkeleton(DashboardCollectionsPage),
      },
      {
        path: 'favorites',
        element: withSkeleton(DashboardFavoritesPage),
      },
      {
        path: 'following',
        element: withSkeleton(DashboardFollowingPage),
      },
      {
        path: 'analytics',
        element: withSkeleton(DashboardAnalyticsPage),
      },
      {
        path: 'settings',
        element: withSkeleton(ProfileSettingsPage),
      },
      {
        path: 'plugin',
        element: withSkeleton(PluginPage),
      },
    ],
  },
  {
    path: '/admin',
    element: (
      <AdminRoute>
        {/* Admin pages use AdminLayout which includes its own sidebar/header */}
        <Outlet />
      </AdminRoute>
    ),
    children: [
      {
        index: true,
        element: withSkeleton(AdminDashboardPage),
      },
      {
        path: 'users/:profileId',
        element: withSkeleton(AdminUserDetailPage),
      },
      {
        path: 'users',
        element: withSkeleton(AdminUsersPage),
      },
      {
        path: 'ban-messages',
        element: withSkeleton(AdminBanMessagesPage),
      },
      {
        path: 'support',
        element: withSkeleton(AdminUserSupportPage),
      },
      {
        path: 'feedback',
        element: withSkeleton(AdminAppFeedbackPage),
      },
      {
        path: 'live-presence',
        element: withSkeleton(AdminLivePresencePage),
      },
      {
        path: 'workflows',
        element: withSkeleton(AdminWorkflowsPage),
      },
      {
        path: 'guides',
        element: withSkeleton(AdminGuidesPage),
      },
      {
        path: 'forums',
        element: withSkeleton(AdminForumsPage),
      },
      {
        path: 'reports',
        element: withSkeleton(AdminReportsPage),
      },
      {
        path: 'reports-export',
        element: withSkeleton(AdminReportsExportPage),
      },
      {
        path: 'roles',
        element: withSkeleton(AdminRolesPage),
      },
      {
        path: 'audit',
        element: withSkeleton(AdminAuditPage),
      },
      {
        path: 'email',
        element: withSkeleton(AdminEmailPage),
      },
      {
        path: 'settings',
        element: withSkeleton(AdminSettingsPage),
      },
    ],
  },
    ],
  },
])

export function AppRouter() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider
          router={router}
          future={{
            v7_startTransition: true,
          }}
        />
      </QueryClientProvider>
    </ErrorBoundary>
  )
}